import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/EditOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'

import { alpha } from '@mui/material/styles'

import { feedbackApi } from '../../services/feedbackApi'
import {
  AppDrawerSection,
  typePillChipProps,
} from '../shared/AppDrawer'
import { poolwerxColors } from '../../theme/colors'
import { COMPACT_TEXTFIELD_SX } from '../../theme/textFieldStyles'
import {
  FEEDBACK_IMPACT_OPTIONS,
  FEEDBACK_STATUS_META,
  FEEDBACK_TYPE_OPTIONS,
  type FeedbackRow,
} from '../../types/feedback'
import { formatRelativeTimestamp } from '../../utils/dateFormatters'

// Mirrors the note-card rhythm in ExceptionDrawer's NoteItem:
// small leading visual (24px), header row that wraps (title + chips +
// right-aligned timestamp), body indented to align with the title,
// hover-revealed action affordance.
const LEAD_SIZE = 24
const LEAD_GAP_PX = 10
const BODY_INDENT_PX = LEAD_SIZE + LEAD_GAP_PX

function labelFor(value: string, options: { value: string; label: string }[]): string {
  return options.find(o => o.value === value)?.label || value
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{
      fontWeight: 600,
      fontSize: '0.65rem',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      mb: 0.25,
      display: 'block',
    }}
  >
    {children}
  </Typography>
)

interface RowProps {
  row: FeedbackRow
  onUpdated: (updated: FeedbackRow) => void
}

const FeedbackRowCard: React.FC<RowProps> = ({ row, onUpdated }) => {
  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(row.title || '')
  const [editDetails, setEditDetails] = useState(row.details)
  const [editTrying, setEditTrying] = useState(row.trying_to_do || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canEdit = row.status === 'new'
  const statusMeta = FEEDBACK_STATUS_META[row.status]
  const dotColor = statusMeta.color
  const statusLabel = statusMeta.label
  const typeLabel = labelFor(row.type, FEEDBACK_TYPE_OPTIONS)
  const impactLabel = labelFor(row.impact, FEEDBACK_IMPACT_OPTIONS)
  const titleText = row.title || row.details.slice(0, 80)
  const hasUpdate = Boolean(row.update_note)

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const updated = await feedbackApi.update(row.id, {
        title: editTitle.trim() || null,
        details: editDetails.trim(),
        trying_to_do: editTrying.trim() || null,
      })
      onUpdated(updated)
      setEditing(false)
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  const handleCardClick = () => {
    if (editing) return
    setExpanded(v => !v)
  }

  return (
    <Paper
      variant="outlined"
      onClick={handleCardClick}
      sx={{
        position: 'relative',
        px: 1.25,
        py: 1.25,
        borderRadius: 1,
        bgcolor: 'background.paper',
        borderColor: 'divider',
        cursor: editing ? 'default' : 'pointer',
        '&:hover .my-feedback-edit, &:focus-within .my-feedback-edit': { opacity: 1 },
      }}
    >
      <Box sx={{ pr: canEdit && !editing ? 3 : 0, position: 'relative', minHeight: LEAD_SIZE }}>
        {/* "Unread" cue - only `new` rows carry the dot so the eye
         * picks them out at a scan. Triaged/closed rows keep the same
         * left indent (so all rows line up) but skip the dot. */}
        {row.status === 'new' && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 6,
              left: 0,
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: dotColor,
              ml: 0.5,
              boxShadow: '0 0 0 3px rgba(0,0,0,0.04)',
            }}
          />
        )}
        <Box sx={{ pl: `${BODY_INDENT_PX}px` }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ minHeight: 18 }}
          >
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: '0.85rem',
                lineHeight: 1.3,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={titleText}
            >
              {titleText}
            </Typography>
            {(() => {
              const ts = formatRelativeTimestamp(row.created_at)
              return (
                <Typography
                  component="time"
                  dateTime={ts.dateTime}
                  title={ts.title}
                  sx={{
                    fontSize: '0.7rem',
                    lineHeight: 1.3,
                    color: 'text.secondary',
                    flexShrink: 0,
                  }}
                >
                  {ts.label}
                </Typography>
              )
            })()}
          </Stack>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
          >
            <Chip
              label={statusLabel}
              size="small"
              variant="outlined"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 600,
                color: statusMeta.color,
                borderColor: statusMeta.color,
                backgroundColor: '#fff',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <Chip
              {...typePillChipProps(typeLabel)}
              sx={{
                height: 18,
                fontSize: '0.65rem',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <Chip
              {...typePillChipProps(impactLabel)}
              sx={{
                height: 18,
                fontSize: '0.65rem',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            {hasUpdate && (
              <Chip
                label="Team update"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: '#fff',
                  bgcolor: poolwerxColors.teal,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Stack>
        </Box>
      </Box>

      {canEdit && !editing && (
        <IconButton
          size="small"
          aria-label="Edit feedback"
          onClick={e => {
            e.stopPropagation()
            setEditing(true)
          }}
          className="my-feedback-edit"
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            p: 0.25,
            opacity: 0,
            transition: 'opacity 120ms ease-out',
            '&:focus-visible': { opacity: 1 },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}

      {!expanded && !editing && (
        <Typography
          sx={{
            mt: 0.5,
            pl: `${BODY_INDENT_PX}px`,
            fontSize: '0.78rem',
            lineHeight: 1.45,
            color: 'text.secondary',
            whiteSpace: 'pre-wrap',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {row.details}
        </Typography>
      )}

      <Collapse in={expanded || editing}>
        <Box
          sx={{ mt: editing ? 2.25 : 0.75, pl: `${BODY_INDENT_PX}px` }}
          onClick={e => e.stopPropagation()}
        >
          {editing ? (
            <Stack spacing={1.75}>
              <FieldLabel>Editing feedback</FieldLabel>
              <TextField
                label="Title"
                size="small"
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                fullWidth
                sx={COMPACT_TEXTFIELD_SX}
              />
              <TextField
                label="Details"
                size="small"
                value={editDetails}
                onChange={e => setEditDetails(e.target.value)}
                multiline
                minRows={3}
                fullWidth
                sx={COMPACT_TEXTFIELD_SX}
              />
              <TextField
                label="What were you trying to do?"
                size="small"
                value={editTrying}
                onChange={e => setEditTrying(e.target.value)}
                multiline
                minRows={1}
                maxRows={5}
                fullWidth
                sx={COMPACT_TEXTFIELD_SX}
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1}>
              <Typography
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                  color: 'text.primary',
                }}
              >
                {row.details}
              </Typography>
              {row.trying_to_do && (
                <Box>
                  <FieldLabel>Trying to do</FieldLabel>
                  <Typography
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                      color: 'text.primary',
                    }}
                  >
                    {row.trying_to_do}
                  </Typography>
                </Box>
              )}
              {row.update_note && (
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: alpha(poolwerxColors.teal, 0.06),
                    border: `1px solid ${alpha(poolwerxColors.teal, 0.25)}`,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="baseline"
                    spacing={0.75}
                    sx={{ mb: 0.25, flexWrap: 'wrap' }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: poolwerxColors.teal }}
                    >
                      Update from the team
                    </Typography>
                    {row.update_note_updated_at &&
                      (() => {
                        const ts = formatRelativeTimestamp(row.update_note_updated_at)
                        return (
                          <Typography
                            component="time"
                            dateTime={ts.dateTime}
                            title={ts.title}
                            variant="caption"
                            color="text.secondary"
                          >
                            · {ts.label}
                          </Typography>
                        )
                      })()}
                  </Stack>
                  <Typography
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                      color: 'text.primary',
                    }}
                  >
                    {row.update_note}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}

interface MyFeedbackListProps {
  /** Feedback rows, owned + fetched by the parent so the tab badge can show a count. */
  rows: FeedbackRow[]
  loading: boolean
  error: string | null
  /** Re-fetch the list (wired to the error-card Retry button). */
  onReload: () => void
  /** Bubble an edited row up so the parent's copy (and the tab count) stays in sync. */
  onUpdated: (updated: FeedbackRow) => void
  onSwitchToSubmit?: () => void
}

const MyFeedbackList: React.FC<MyFeedbackListProps> = ({
  rows,
  loading,
  error,
  onReload,
  onUpdated,
  onSwitchToSubmit,
}) => {
  if (loading) {
    return (
      <Stack spacing={0.75}>
        {[0, 1, 2].map(i => (
          <Skeleton key={i} variant="rounded" height={68} sx={{ borderRadius: 1 }} />
        ))}
      </Stack>
    )
  }

  if (error) {
    return (
      <AppDrawerSection
        sx={{ borderColor: 'error.light' }}
        title="Couldn't load"
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon fontSize="small" />}
            onClick={onReload}
          >
            Retry
          </Button>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
          {error}
        </Typography>
      </AppDrawerSection>
    )
  }

  if (rows.length === 0) {
    return (
      <AppDrawerSection
        sx={{
          borderStyle: 'dashed',
          borderColor: 'rgba(0, 0, 0, 0.16)',
          bgcolor: 'transparent',
        }}
        bodyPadding={{ px: 2, pt: 2, pb: 2, textAlign: 'center' }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, fontSize: '0.82rem' }}>
          You haven't sent any feedback yet.
        </Typography>
        {onSwitchToSubmit && (
          <Button size="small" variant="contained" onClick={onSwitchToSubmit}>
            Give feedback
          </Button>
        )}
      </AppDrawerSection>
    )
  }

  return (
    <Stack spacing={0.75}>
      {rows.map(row => (
        <FeedbackRowCard key={row.id} row={row} onUpdated={onUpdated} />
      ))}
    </Stack>
  )
}

export default MyFeedbackList
